(() => {
  function shuffleItems(items) {
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
  }

  function youtubeId(value) {
    try {
      const url = new URL(value);
      if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return null;
      const host = url.hostname.replace(/^www\./, '');
      let id;
      if (host === 'youtu.be') id = url.pathname.slice(1);
      else if (['youtube.com', 'm.youtube.com', 'youtube-nocookie.com'].includes(host)) {
        if (url.pathname === '/watch') id = url.searchParams.get('v');
        else id = url.pathname.match(/^\/(?:embed|shorts|live)\/([^/]+)\/?$/)?.[1];
      }
      return /^[\w-]{11}$/.test(id || '') ? id : null;
    } catch { return null; }
  }

  class ProductVideos extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.items = [];
      const seen = new Set();
      this.querySelectorAll('[data-video-link]').forEach((link) => {
        const id = youtubeId(link.getAttribute('href'));
        if (!id || seen.has(id)) { link.remove(); return; }
        seen.add(id);
        link.href = `https://www.youtube.com/watch?v=${id}`;
        link.setAttribute('role', 'button');
        link.setAttribute('aria-pressed', 'false');
        const poster = document.createElement('img');
        poster.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
        poster.alt = '';
        poster.width = 480;
        poster.height = 360;
        poster.loading = 'lazy';
        link.querySelector('img')?.remove();
        link.prepend(poster);
        const item = { id, link, title: link.querySelector('[data-title]').textContent, author: '' };
        this.items.push(item);
        link.addEventListener('click', (event) => {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.select(item);
        }, { signal });
        link.addEventListener('keydown', (event) => {
          if (event.key === ' ') { event.preventDefault(); link.click(); }
        }, { signal });
      });
      if (!this.items.length) {
        if (this.hasAttribute('data-editor')) this.querySelector('[data-empty]').hidden = false;
        else this.closest('.shopify-section').hidden = true;
        return;
      }
      if (this.hasAttribute('data-random-order') && !this.shuffled && this.items.length > 1) {
        shuffleItems(this.items);
        this.querySelector('.product-videos__list').append(...this.items.map(({ link }) => link));
        this.shuffled = true;
      }
      this.toggleAttribute('data-single', this.items.length === 1);
      this.querySelector('[data-empty]').hidden = true;
      this.querySelector('.product-videos__list').hidden = this.items.length === 1;
      this.querySelector('[data-main]').hidden = false;
      this.select(this.items[0]);
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.items.forEach((item) => this.loadTitle(item, signal));
      }, { rootMargin: '200px' });
      this.observer.observe(this);
    }

    select(item) {
      if (this.active === item) return;
      this.active = item;
      this.items.forEach(({ link }) => link.setAttribute('aria-pressed', String(link === item.link)));
      const media = this.querySelector('[data-media-template]').content.firstElementChild.cloneNode(true);
      const poster = media.querySelector('img');
      const fallback = `https://i.ytimg.com/vi/${item.id}/hqdefault.jpg`;
      poster.addEventListener('error', () => { poster.src = fallback; }, { once: true });
      poster.addEventListener('load', () => {
        if (poster.naturalWidth <= 120 && poster.src !== fallback) poster.src = fallback;
      });
      poster.src = `https://i.ytimg.com/vi/${item.id}/maxresdefault.jpg`;
      const frame = media.querySelector('template').content.querySelector('iframe');
      frame.title = item.title;
      frame.src = `https://www.youtube.com/embed/${item.id}?playsinline=1&enablejsapi=1&rel=0&origin=${encodeURIComponent(location.origin)}`;
      this.querySelector('[data-player]').replaceChildren(media);
      this.updateCaption();
    }

    updateCaption() {
      this.querySelector('[data-current-title]').textContent = this.active.title;
      this.querySelector('[data-current-author]').textContent = this.active.author;
      this.querySelector('[data-watch]').href = this.active.link.href;
    }

    async loadTitle(item, signal) {
      try {
        const response = await fetch(`https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(item.link.href)}`, { signal: AbortSignal.any([signal, AbortSignal.timeout(6000)]) });
        if (!response.ok) return;
        const data = await response.json();
        if (signal.aborted || typeof data.title !== 'string' || !data.title.trim()) return;
        item.title = data.title;
        item.author = typeof data.author_name === 'string' ? data.author_name : '';
        item.link.querySelector('[data-title]').textContent = item.title;
        item.link.querySelector('[data-author]').textContent = item.author;
        if (this.active === item) {
          this.updateCaption();
          const media = this.querySelector('[data-player] video-media');
          const frame = media.querySelector('iframe') || media.querySelector('template')?.content.querySelector('iframe');
          if (frame) frame.title = item.title;
        }
      } catch { /* Keep the translated fallback title when YouTube is unavailable. */ }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = null;
      this.active = null;
    }
  }
  if (!customElements.get('product-videos')) customElements.define('product-videos', ProductVideos);
})();
